'use client';

import { RoomContext } from '@livekit/components-react';
import { Room, RoomEvent } from 'livekit-client';
import React from 'react';
import { DEFAULT_LIVE_PROFILE, type LiveProfile } from '@/lib/live-profile';
import { ApiError, LiveConnectionDetails } from '@/lib/live-types';
import styles from '@/styles/Live.module.css';
import { LiveStage } from './LiveStage';

type ViewerPhase = 'name' | 'connecting' | 'connected' | 'exited' | 'error';

const VIEWER_NAME_KEY = 'live-viewer-name';

async function createViewerRoom(displayName: string) {
  const liveRoom = new Room({
    adaptiveStream: { pixelDensity: 1, pauseVideoInBackground: true },
    dynacast: true,
  });

  try {
    const response = await fetch('/api/live/token/viewer', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name: displayName }),
    });
    const data = (await response.json()) as LiveConnectionDetails & ApiError;
    if (!response.ok) throw new Error(data.error || '无法进入直播间。');
    await liveRoom.connect(data.serverUrl, data.participantToken, {
      autoSubscribe: true,
      websocketTimeout: 15000,
      peerConnectionTimeout: 30000,
      maxRetries: 3,
    });
    return { liveRoom, data };
  } catch (cause) {
    await liveRoom.disconnect();
    throw cause;
  }
}

function viewerConnectionError(cause: unknown) {
  const message = cause instanceof Error ? cause.message : '';
  return /signal|fetch|network|websocket|connect/i.test(message)
    ? '直播服务暂时无法连接，请稍后重试。'
    : message || '无法进入直播间。';
}

export function ViewerClient() {
  const [phase, setPhase] = React.useState<ViewerPhase>('name');
  const [name, setName] = React.useState('');
  const [error, setError] = React.useState('');
  const [room, setRoom] = React.useState<Room>();
  const [hostIdentity, setHostIdentity] = React.useState('host');
  const [liveProfile, setLiveProfile] = React.useState<LiveProfile>(DEFAULT_LIVE_PROFILE);
  const roomRef = React.useRef<Room>();
  const closedByUser = React.useRef(false);
  const recoveringRoom = React.useRef(false);
  const ignoredDisconnects = React.useRef(new WeakSet<Room>());

  React.useEffect(() => {
    setName(window.localStorage.getItem(VIEWER_NAME_KEY) || '');
  }, []);

  const watchUnexpectedDisconnect = React.useCallback((liveRoom: Room) => {
    liveRoom.on(RoomEvent.Disconnected, () => {
      if (
        !closedByUser.current &&
        !ignoredDisconnects.current.has(liveRoom) &&
        roomRef.current === liveRoom
      ) {
        setError('直播连接已断开，请重新进入。');
        setPhase('error');
      }
    });
  }, []);

  const connect = React.useCallback(async () => {
    const displayName = name.trim().slice(0, 24);
    if (!displayName) {
      setPhase('name');
      return;
    }

    setPhase('connecting');
    setError('');
    window.localStorage.setItem(VIEWER_NAME_KEY, displayName);
    closedByUser.current = false;

    try {
      const { liveRoom, data } = await createViewerRoom(displayName);
      if (closedByUser.current) {
        ignoredDisconnects.current.add(liveRoom);
        await liveRoom.disconnect();
        return;
      }
      watchUnexpectedDisconnect(liveRoom);
      roomRef.current = liveRoom;
      setRoom(liveRoom);
      setHostIdentity(data.hostIdentity);
      setLiveProfile(data.liveProfile || DEFAULT_LIVE_PROFILE);
      setPhase('connected');
    } catch (cause) {
      setError(viewerConnectionError(cause));
      setPhase('error');
    }
  }, [name, watchUnexpectedDisconnect]);

  const recoverForNextBroadcast = React.useCallback(
    async (staleRoom: Room) => {
      if (closedByUser.current || recoveringRoom.current || roomRef.current !== staleRoom) {
        return;
      }

      recoveringRoom.current = true;
      ignoredDisconnects.current.add(staleRoom);

      try {
        await staleRoom.disconnect();
        if (closedByUser.current) return;

        const displayName = (name.trim() || '观众').slice(0, 24);
        const { liveRoom, data } = await createViewerRoom(displayName);
        if (closedByUser.current) {
          ignoredDisconnects.current.add(liveRoom);
          await liveRoom.disconnect();
          return;
        }

        watchUnexpectedDisconnect(liveRoom);
        roomRef.current = liveRoom;
        setRoom(liveRoom);
        setHostIdentity(data.hostIdentity);
        setLiveProfile(data.liveProfile || DEFAULT_LIVE_PROFILE);
      } catch (cause) {
        if (!closedByUser.current) {
          setError(viewerConnectionError(cause));
          setPhase('error');
        }
      } finally {
        recoveringRoom.current = false;
      }
    },
    [name, watchUnexpectedDisconnect],
  );

  React.useEffect(() => {
    if (!room || phase !== 'connected') return;

    const handleHostDisconnected = (participant: { identity: string }) => {
      if (participant.identity === hostIdentity) void recoverForNextBroadcast(room);
    };
    room.on(RoomEvent.ParticipantDisconnected, handleHostDisconnected);
    return () => {
      room.off(RoomEvent.ParticipantDisconnected, handleHostDisconnected);
    };
  }, [hostIdentity, phase, recoverForNextBroadcast, room]);

  React.useEffect(() => {
    return () => {
      closedByUser.current = true;
      roomRef.current?.disconnect();
    };
  }, []);

  const submitName = (event: React.FormEvent) => {
    event.preventDefault();
    void connect();
  };

  const exit = async () => {
    closedByUser.current = true;
    const liveRoom = roomRef.current;
    if (liveRoom) ignoredDisconnects.current.add(liveRoom);
    await liveRoom?.disconnect();
    roomRef.current = undefined;
    setRoom(undefined);
    setPhase('exited');
  };

  if (room && phase === 'connected') {
    return (
      <RoomContext.Provider value={room}>
        <LiveStage
          room={room}
          role="viewer"
          hostIdentity={hostIdentity}
          liveProfile={liveProfile}
          onClose={exit}
        />
      </RoomContext.Provider>
    );
  }

  return (
    <main className={styles.gatePage}>
      <div className={styles.gateGlow} />
      <section className={styles.gateCard}>
        <div className={styles.brandMark}>LIVE</div>
        <span className={styles.eyebrow}>我的直播间</span>
        <h1>
          {phase === 'name'
            ? '先告诉大家你的昵称'
            : phase === 'exited'
              ? '已退出直播间'
              : phase === 'error'
                ? '暂时无法进入'
                : '正在进入直播间'}
        </h1>
        <p>
          {phase === 'name'
            ? '进入后，你的聊天消息会显示这个名字。'
            : phase === 'exited'
              ? '感谢观看，随时可以重新进入。'
              : error || '正在建立安全直播连接…'}
        </p>
        {phase === 'name' ? (
          <form className={styles.loginForm} onSubmit={submitName}>
            <label htmlFor="viewer-name">观众昵称</label>
            <input
              id="viewer-name"
              value={name}
              onChange={(event) => setName(event.target.value.slice(0, 24))}
              placeholder="请输入昵称"
              maxLength={24}
              autoComplete="nickname"
              enterKeyHint="go"
              autoFocus
              required
            />
            <button type="submit" disabled={!name.trim()}>
              进入直播间
            </button>
          </form>
        ) : phase === 'connecting' ? (
          <div className={styles.connecting}>
            <span />
            连接中…
          </div>
        ) : (
          <div className={styles.readyActions}>
            <button type="button" onClick={phase === 'error' ? connect : () => setPhase('name')}>
              {phase === 'error' ? '重新连接' : '重新进入'}
            </button>
            {phase === 'error' && (
              <button
                className={styles.secondaryButton}
                type="button"
                onClick={() => setPhase('name')}
              >
                修改昵称
              </button>
            )}
          </div>
        )}
      </section>
    </main>
  );
}
